import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";
import { STATUS_LABEL, _numToIndianWords } from "./constants";
import { SignatureCapture } from "./SignatureCapture";

export function XlsxEditButtons({ docId, onImported }) {
  const fileRef = React.useRef(null);
  const exportDoc = async () => {
    try {
      const res = await api.get(`/hr/documents/${docId}/xlsx`, { responseType: "blob" });
      const disp = res.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `${docId}_edit.xlsx`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Excel exported — edit amounts + open 'CTC Break-down Preview' sheet to see the live break-down");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Excel export failed");
    }
  };
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/hr/documents/${docId}/xlsx`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const n = data.changed_count || 0;
      toast.success(n ? `${n} field${n === 1 ? "" : "s"} updated from Excel` : "No changes detected");
      if (n) onImported && onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Excel import failed");
    } finally {
      e.target.value = "";
    }
  };
  return (
    <>
      <button
        onClick={exportDoc}
        className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300"
        data-testid="doc-export-xlsx"
        title="Download this doc's current CTC/amounts as Excel with live break-down preview"
      >
        <Download className="w-3 h-3" /> EXPORT XLSX
      </button>
      <button
        onClick={() => fileRef.current?.click()}
        className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300"
        data-testid="doc-import-xlsx"
        title="Upload the edited Excel — merges non-blank cells into this doc's fields"
      >
        <Upload className="w-3 h-3" /> IMPORT XLSX
      </button>
      <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onFilePicked} data-testid="doc-import-xlsx-file" />
    </>
  );
}


export function DocPreviewModal({ doc, title, onClose }) {
  const [pages, setPages] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/hr/documents/${doc.id}/preview`, { timeout: 120000 })
      .then((r) => { if (alive) setPages(r.data.pages || []); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Preview failed"); });
    return () => { alive = false; };
  }, [doc.id]);
  return (
    <div className="fixed inset-0 bg-zinc-900/70 z-[70] flex items-start justify-center p-2 sm:p-4 pt-[64px] overflow-y-auto" data-testid="doc-preview-modal">
      <div
        className="bg-zinc-100 border-2 border-zinc-900 rounded-sm w-full max-w-3xl max-h-[92vh] flex flex-col select-none"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b-2 border-zinc-900 bg-white">
          <div className="min-w-0">
            <div className="label-mono">READ-ONLY PREVIEW</div>
            <div className="font-heading text-sm font-black truncate">{title}</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {pages && <span className="text-[10px] font-mono text-zinc-500">{pages.length} page{pages.length === 1 ? "" : "s"}</span>}
            <button onClick={onClose} className="brut-btn-secondary px-2 py-1 text-xs" data-testid="doc-preview-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="overflow-y-auto p-3 space-y-3" style={{ WebkitUserSelect: "none" }}>
          {err && <div className="text-xs font-mono text-rose-600 p-4 text-center" data-testid="doc-preview-error">{err}</div>}
          {!pages && !err && <div className="text-xs font-mono text-zinc-500 p-8 text-center animate-pulse">Rendering document…</div>}
          {pages && pages.map((src, i) => (
            <img key={i} src={src} alt={`Page ${i + 1}`} draggable={false} className="w-full border border-zinc-300 shadow-sm pointer-events-none" />
          ))}
        </div>
      </div>
    </div>
  );
}

export function DocDetailModal({ doc, templates, isAdmin, isTrueAdmin, currentUserId, onClose, onChanged, group, onSwitch }) {
  const tpl = templates.find((t) => t.key === doc.template_type);
  const isEmployee = doc.employee_id === currentUserId;
  const groupIdx = group ? group.findIndex((g) => g.id === doc.id) : -1;
  const nextInGroup = group && groupIdx >= 0 && groupIdx < group.length - 1 ? group[groupIdx + 1] : null;
  const [values, setValues] = useState(() => {
    // Seed `night_shift_eligible` from any legacy `night_shift_allow` value so
    // opening an OLD Offer/Appointment/Increment doc doesn't silently
    // reset Night Shift to 0 the next time HR changes the CTC.
    const v = { ...(doc.field_values || {}) };
    if (v.night_shift_eligible === undefined) {
      v.night_shift_eligible = Number(v.night_shift_allow || 0) > 0;
    }
    return v;
  });
  const [fileNo, setFileNo] = useState(doc.file_no || "");
  const [docDate, setDocDate] = useState(doc.doc_date || "");
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [otpStep, setOtpStep] = useState(null);
  const [otpValue, setOtpValue] = useState("");
  const bothSigned = !!(doc.signed_at_employee && doc.signed_at_hr);
  const editable = (doc.signed_at_hr || doc.hr_signature_b64 || doc.status === "completed")
    ? isTrueAdmin
    : (isAdmin || isEmployee);

  // Auto-generate File No. in the canonical format LDxxxx/MM/YYYY the first
  // time the modal opens on a doc that doesn't have one yet. Uses the
  // employee's own SalaryBox code + the current month/year. Admin can
  // override in the top-level input; the same value is mirrored into the
  // template `file_no` field so both places render identically in the PDF.
  useEffect(() => {
    if (fileNo) {
      // Keep the inline template field in sync whenever admin edits the top-level box
      setValues((v) => (v.file_no === fileNo ? v : { ...v, file_no: fileNo }));
      return;
    }
    const empCode = (doc.employee_code || doc.employee_id_at_create || values.employee_code || "").toString().trim();
    if (!empCode) return;
    const today = new Date();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    const generated = `${empCode}/${mm}/${yyyy}`.toUpperCase();
    setFileNo(generated);
    setValues((v) => ({ ...v, file_no: generated }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNo, doc.id]);

  const updateField = (k, v) => {
    setValues((s) => {
      const next = { ...s, [k]: v };
      // Recompute the full CTC break-up + In Hand Salary whenever ANY
      // driver field changes. Triggers:
      //   • ctc_monthly            → forward: derive all fields from CTC
      //   • in_hand_salary         → reverse: derive CTC from target In-Hand,
      //                              then forward-recalc everything so the
      //                              math is guaranteed self-consistent.
      //   • night_shift_eligible   → toggles Night Shift ₹1,000 ↔ 0. Respects
      //                              whichever driver was last used so the
      //                              locked figure stays put.
      //   • pt                     → In Hand Salary only (statutory tax edit)
      // Every action re-runs the math so HR never sees stale values.
      const salaryTemplates = ["offer_letter", "appointment_letter", "increment_letter"];
      const isCtcChange     = (k === "ctc_monthly");
      const isInHandChange  = (k === "in_hand_salary");
      const isNightToggle   = (k === "night_shift_eligible");
      const isPtChange      = (k === "pt");
      if ((isCtcChange || isInHandChange || isNightToggle || isPtChange) && salaryTemplates.includes(doc.template_type)) {
        // Remember which figure the user is anchoring against so subsequent
        // Night-Shift or PT edits preserve the correct field.
        if (isCtcChange)    next._salary_driver = "ctc";
        if (isInHandChange) next._salary_driver = "in_hand";
        const driver = next._salary_driver || (Number(next.in_hand_salary) > 0 && !Number(next.ctc_monthly) ? "in_hand" : "ctc");
        const nightEligible = !!next.night_shift_eligible;
        const nightShift = nightEligible ? 1000 : 0;
        const pt = Math.max(0, Math.round(Number(isPtChange ? v : (next.pt ?? 200)) || 0));

        // ---- Forward: given monthly CTC (M), return every field ----
        // Mirrors backend _compute_breakup EXACTLY so the form's In-Hand is
        // identical to the letter's "Net Salary (Gross − Total deductions)".
        // CTC entered = Gross payout base; Night Shift is a component INSIDE
        // the CTC; employer PF/ESI ride on top (shown separately, not summed).
        const forwardCalc = (M) => {
          const basic = Math.round(M * 0.5);
          const hra = Math.round(basic * 0.4);
          const conveyance = 1600;
          const medical = 1250;
          // Statutory wage base = Gross − HRA (PF capped ₹1,800). ESIC eligibility
          // is on FULL monthly gross ≤ ₹21,000 — above that, no ESIC at all.
          const statWage = Math.max(0, M - hra);
          const pfEmployer = Math.min(1800, Math.round(statWage * 0.12));
          const pfEmployee = pfEmployer;
          let esicEmployer = 0, esicEmployee = 0;
          if (M <= 21000) {
            esicEmployer = Math.round(statWage * 0.0325);
            esicEmployee = Math.round(statWage * 0.0075);
          }
          const special = Math.max(0, M - basic - hra - conveyance - medical - nightShift);
          const totalMonthly = M;
          const annual = M * 12;
          const inHand = Math.max(0, M - pfEmployee - esicEmployee - pt);
          return { basic, hra, conveyance, medical, pfEmployer, pfEmployee,
                   esicEmployer, esicEmployee, special, totalMonthly, annual, inHand };
        };

        // ---- Reverse: binary-search for M such that forwardCalc(M).inHand == target ----
        const reverseFromInHand = (targetInHand) => {
          if (!(targetInHand > 0)) return 0;
          let lo = 0, hi = Math.max(200_000, targetInHand * 3);
          for (let iter = 0; iter < 40 && lo < hi; iter++) {
            const mid = Math.floor((lo + hi) / 2);
            const y = forwardCalc(mid).inHand;
            if (y < targetInHand) lo = mid + 1;
            else hi = mid;
          }
          return lo;
        };

        let M;
        if (driver === "in_hand") {
          const target = Math.max(0, Math.round(Number(next.in_hand_salary) || 0));
          M = reverseFromInHand(target);
        } else {
          M = Math.max(0, Math.round(Number(next.ctc_monthly) || 0));
        }

        if (M > 0) {
          const r = forwardCalc(M);
          Object.assign(next, {
            ctc_monthly: M,
            basic_salary: r.basic,
            hra: r.hra,
            conveyance: r.conveyance,
            medical_allow: r.medical,
            night_shift_allow: nightShift,
            special_allow: r.special,
            pf_employee: r.pfEmployee,
            pf_employer: r.pfEmployer,
            esi_employee: r.esicEmployee,
            esi_employer: r.esicEmployer,
            pt,
            in_hand_salary: r.inHand,
            ctc_annual: r.annual,
            ctc_words: _numToIndianWords(r.annual),
            ctc_monthly_words: _numToIndianWords(r.totalMonthly),
            new_ctc_annual: r.annual,
            new_ctc_words: _numToIndianWords(r.annual),
          });
        }
      }
      return next;
    });
  };
  const saveFields = async () => {
    setSaving(true);
    try {
      const payload = { field_values: values };
      if (isAdmin) {
        if (fileNo && fileNo !== doc.file_no) payload.file_no = fileNo;
        if (docDate && docDate !== doc.doc_date) payload.doc_date = docDate;
      }
      await api.patch(`/hr/documents/${doc.id}`, payload);
      toast.success("Saved");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const saveAndNext = async () => {
    setSaving(true);
    try {
      const payload = { field_values: values };
      if (isAdmin) {
        if (fileNo && fileNo !== doc.file_no) payload.file_no = fileNo;
        if (docDate && docDate !== doc.doc_date) payload.doc_date = docDate;
      }
      await api.patch(`/hr/documents/${doc.id}`, payload);
      toast.success(`Saved — moving to ${templates.find((t) => t.key === nextInGroup.template_type)?.title || "next document"}`);
      onSwitch && onSwitch(nextInGroup);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const autoFillFromSalaryBox = async () => {
    try {
      const { data } = await api.get(`/hr/autofill/${doc.employee_id}`, { params: { template_key: doc.template_type } });
      const patch = data?.values || {};
      const filled = Object.keys(patch).length;
      if (filled === 0) {
        toast.info("No SalaryBox salary data on file for this employee — set it via Users → SalaryBox Import first.");
        return;
      }
      // Merge, but only fill fields that are currently blank so we don't clobber HR's edits
      setValues((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(patch)) {
          if (next[k] === undefined || next[k] === "" || next[k] === null) next[k] = patch[k];
        }
        return next;
      });
      toast.success(`Auto-filled ${filled} field${filled === 1 ? "" : "s"} from SalaryBox`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fill failed");
    }
  };
  const sendToEmployee = async () => {
    try {
      await api.post(`/hr/documents/${doc.id}/send-to-employee`);
      toast.success("Sent to employee");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const requestOtp = async () => {
    const { data } = await api.post(`/hr/documents/${doc.id}/sign/request-otp`);
    if (data.otp_required) {
      setOtpStep({ sentTo: data.sent_to });
      setOtpValue("");
      toast.info(`Verification code sent to ${data.sent_to}`);
    }
    return data;
  };
  const sign = async (endpoint, otp) => {
    if (!window.__sig) { toast.error("Please provide a signature (draw, upload, or use saved)"); return; }
    setSigning(true);
    try {
      await api.post(`/hr/documents/${doc.id}/${endpoint}`, {
        signature_b64: window.__sig,
        stamp_b64: window.__stamp || undefined,
        save_to_profile: !!window.__sigSave,
        otp: otp || undefined,
      });
      toast.success("Signed" + (window.__stamp ? " with stamp override" : ""));
      window.__sig = null; window.__stamp = null; window.__sigSave = false;
      setOtpStep(null); setOtpValue("");
      onChanged();
    } catch (e) {
      if (e.response?.status === 428) {
        // Email-OTP verification required before the signature is accepted
        try {
          const data = await requestOtp();
          if (!data.otp_required) toast.info("Please try signing again");
        } catch (e2) { toast.error(e2.response?.data?.detail || "Could not send verification code"); }
      } else {
        toast.error(e.response?.data?.detail || "Sign failed");
      }
    }
    setSigning(false);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-start justify-center p-2 sm:p-3 pt-[64px] overflow-y-auto">
      <div className="brut-card max-w-3xl w-full max-h-[92vh] flex flex-col bg-white" onClick={(e) => e.stopPropagation()} data-testid="doc-detail-modal">
        <div className="p-3 border-b-2 border-zinc-900">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-mono">{tpl?.category || "—"}</div>
              <h3 className="font-heading text-lg font-black">{tpl?.title || doc.template_type}</h3>
            </div>
            <button onClick={onClose}><X className="w-4 h-4" /></button>
          </div>
          {group && group.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="doc-group-pills">
              {group.map((g, i) => {
                const gt = templates.find((t) => t.key === g.template_type);
                const active = g.id === doc.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => !active && onSwitch && onSwitch(g)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-mono font-bold border-2 transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}
                    data-testid={`doc-group-pill-${g.template_type}`}
                    title={active ? "Currently editing" : `Switch to ${gt?.title || g.template_type}`}
                  >
                    {i + 1}. {gt?.title || g.template_type}
                  </button>
                );
              })}
              <span className="text-[9px] font-mono text-zinc-400 ml-1">{groupIdx + 1} of {group.length}</span>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="flex items-center gap-3 text-xs font-mono">
            <span className={`px-2 py-0.5 rounded-full font-bold ${STATUS_LABEL[doc.status]?.cls || "bg-zinc-100"}`}>
              {STATUS_LABEL[doc.status]?.label || doc.status}
            </span>
            <span className="text-zinc-500">Employee: {doc.employee_name_at_create}</span>
          </div>

          {/* Fields */}
          <div className="brut-card bg-zinc-50 p-3">
            <div className="label-mono mb-2">DOCUMENT METADATA</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              <div>
                <label className="label-mono block text-zinc-600">DATE {isAdmin && <span className="text-[9px] text-zinc-400">(auto-set on HR sign · admin can override)</span>}</label>
                <input
                  type="date"
                  value={docDate || ""}
                  onChange={(e) => setDocDate(e.target.value)}
                  disabled={!editable || !isAdmin}
                  className="brut-input w-full text-sm mt-0.5 font-mono"
                  data-testid="doc-date-input"
                />
              </div>
              <div>
                <label className="label-mono block text-zinc-600">FILE NO. {isAdmin && <span className="text-[9px] text-zinc-400">(auto = EMP-CODE/MM/YYYY · admin can override)</span>}</label>
                <input
                  type="text"
                  value={fileNo || ""}
                  onChange={(e) => setFileNo(e.target.value)}
                  disabled={!editable || !isAdmin}
                  placeholder="e.g. LD013/04/2026"
                  className="brut-input w-full text-sm mt-0.5 font-mono uppercase"
                  data-testid="doc-file-no-input"
                />
              </div>
            </div>
            <div className="label-mono mb-2">FIELDS</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(tpl?.fields || []).map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="label-mono block text-zinc-600">{f.label}{f.required && <span className="text-rose-600">*</span>}</label>
                  {f.type === "textarea" ? (
                    <textarea value={values[f.key] || ""} onChange={(e) => updateField(f.key, e.target.value)}
                      disabled={!editable} rows={2}
                      className="brut-input w-full text-sm mt-0.5"
                      data-testid={`doc-field-${f.key}`} />
                  ) : f.type === "select" ? (
                    <select value={values[f.key] || ""} onChange={(e) => updateField(f.key, e.target.value)}
                      disabled={!editable} className="brut-input w-full text-sm mt-0.5"
                      data-testid={`doc-field-${f.key}`}>
                      <option value="">—</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <label className="brut-input w-full text-sm mt-0.5 flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!values[f.key]}
                        onChange={(e) => updateField(f.key, e.target.checked)}
                        disabled={!editable}
                        className="w-4 h-4"
                        data-testid={`doc-field-${f.key}`}
                      />
                      <span className="text-zinc-700">{!!values[f.key] ? "Yes — allowance applies" : "No — not eligible"}</span>
                    </label>
                  ) : (
                    <input type={f.type === "number" ? "number" : f.type === "date" ? "text" : "text"}
                      value={values[f.key] ?? ""} onChange={(e) => updateField(f.key, e.target.value)}
                      disabled={!editable} placeholder={f.help || ""}
                      className="brut-input w-full text-sm mt-0.5"
                      data-testid={`doc-field-${f.key}`} />
                  )}
                </div>
              ))}
            </div>
            {editable && (
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={saveFields} disabled={saving} className="brut-btn px-3 py-1.5 text-xs" data-testid="doc-save-fields">
                  {saving ? "SAVING…" : "SAVE FIELDS"}
                </button>
                {isAdmin && (
                  <button
                    onClick={autoFillFromSalaryBox}
                    className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-amber-100 hover:bg-amber-200"
                    data-testid="doc-autofill-salarybox"
                    title="Pull CTC / basic / HRA / PF / ESIC from this employee's SalaryBox record (only fills blank fields)"
                  >
                    <Sparkles className="w-3 h-3" /> AUTO-FILL FROM SALARYBOX
                  </button>
                )}
                {isAdmin && ["offer_letter","appointment_letter","increment_letter"].includes(doc.template_type) && (
                  <XlsxEditButtons docId={doc.id} onImported={onChanged} />
                )}
              </div>
            )}
          </div>

          {/* Signature status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <SigStatus label="Employee Signature" who={doc.employee_name_at_create} at={doc.signed_at_employee} present={!!doc.employee_signature_b64} />
            <SigStatus label="HR Signature" who="Land Drafters — HR" at={doc.signed_at_hr} present={!!doc.hr_signature_b64} />
          </div>

          {/* Signature pad — employee: whenever their signature is missing;
              HR/admin: while awaiting HR. */}
          {((isEmployee && !doc.employee_signature_b64) ||
            (isAdmin && !doc.hr_signature_b64 && (doc.status === "pending_hr" || doc.status === "draft"))
          ) && (
            <div className="brut-card bg-white p-3" data-testid="sig-section">
              <SignatureCapture isHR={isAdmin} />
              <div className="flex justify-end gap-2 mt-2">
                {isEmployee && !doc.employee_signature_b64 && !otpStep && (
                  <button onClick={() => sign("sign-employee")} disabled={signing} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300" data-testid="sign-as-employee">
                    <PenLine className="w-3 h-3 inline mr-1" /> SIGN AS EMPLOYEE
                  </button>
                )}
                {isAdmin && (doc.status === "pending_hr" || doc.status === "draft") && (
                  <button onClick={() => sign("sign-hr")} disabled={signing} className="brut-btn px-3 py-1.5 text-xs bg-emerald-300" data-testid="sign-as-hr">
                    <PenLine className="w-3 h-3 inline mr-1" /> SIGN AS HR
                  </button>
                )}
              </div>
              {otpStep && (
                <div className="flex flex-wrap items-center justify-end gap-2 mt-2 pt-2 border-t border-zinc-200" data-testid="sign-otp-row">
                  <span className="text-[10px] font-mono text-zinc-500">Enter the 6-digit code sent to {otpStep.sentTo}</span>
                  <input
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    className="brut-input w-28 text-sm font-mono tracking-widest"
                    data-testid="sign-otp-input"
                  />
                  <button
                    onClick={() => sign("sign-employee", otpValue)}
                    disabled={signing || otpValue.length !== 6}
                    className="brut-btn px-3 py-1.5 text-xs bg-emerald-300 disabled:opacity-50"
                    data-testid="sign-otp-verify">
                    VERIFY &amp; SIGN
                  </button>
                  <button
                    onClick={async () => { try { await requestOtp(); } catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); } }}
                    className="text-[10px] font-mono underline text-zinc-600 hover:text-zinc-900"
                    data-testid="sign-otp-resend">
                    Resend code
                  </button>
                </div>
              )}
            </div>
          )}

          {/* HR actions */}
          {isAdmin && doc.status === "draft" && (
            <button onClick={sendToEmployee} className="brut-btn px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="doc-send">
              <Send className="w-3 h-3" /> SEND TO EMPLOYEE
            </button>
          )}

          {/* Download + View + Delete */}
          <div className="flex items-center gap-2 flex-wrap">
            {(isAdmin || bothSigned) ? (
            <button
              onClick={async () => {
                // SEC-004 fix: authenticated blob download instead of `?auth=<token>` in URL
                const tid = toast.loading("Preparing PDF…");
                try {
                  const res = await api.get(`/hr/documents/${doc.id}/pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  const title = (tpl?.title || doc.template_type || "document").replace(/\s+/g, "_");
                  a.download = `${title}_${doc.id}.pdf`;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                  toast.success("PDF downloaded", { id: tid });
                } catch (e) { toast.error(e.response?.data?.detail || "PDF download failed", { id: tid }); }
              }}
              className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1"
              data-testid="doc-download-pdf">
              <Download className="w-3 h-3" /> DOWNLOAD PDF
            </button>
            ) : (
            <span className="text-[10px] font-mono text-zinc-500 inline-flex items-center gap-1 border border-zinc-300 rounded-sm px-2 py-1 bg-zinc-50" data-testid="doc-download-locked" title="Strictly private — download unlocks once BOTH you and HR have signed">
              <Lock className="w-3 h-3" /> DOWNLOAD UNLOCKS AFTER BOTH SIGNATURES
            </span>
            )}
            {(isAdmin || bothSigned) && (
              isAdmin || (doc.nosign_request?.status === "approved") ? (
                <button
                  onClick={async () => {
                    const tid = toast.loading("Preparing no-sign PDF…");
                    try {
                      const res = await api.get(`/hr/documents/${doc.id}/pdf`, { params: { without_employee_sign: true }, responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${(tpl?.title || doc.template_type || "document").replace(/\s+/g, "_")}_NoEmpSign.pdf`;
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                      toast.success("No-sign PDF downloaded", { id: tid });
                    } catch (e) { toast.error(e.response?.data?.detail || "Download failed", { id: tid }); }
                  }}
                  className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 border-purple-700 text-purple-800 hover:bg-purple-50"
                  data-testid="doc-download-nosign" title="Download this document WITHOUT the employee signature">
                  <Download className="w-3 h-3" /> PDF · NO EMP SIGN
                </button>
              ) : doc.nosign_request?.status === "pending" ? (
                <span className="text-[10px] font-mono text-amber-700 border border-amber-600 bg-amber-50 px-2 py-1" data-testid="nosign-pending-pill">NO-SIGN: AWAITING ADMIN APPROVAL</span>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      await api.post(`/hr/documents/${doc.id}/request-nosign`);
                      toast.success("Request sent — admin will approve or deny");
                      onChanged && onChanged();
                    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
                  }}
                  className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 border-purple-700 text-purple-800 hover:bg-purple-50"
                  data-testid="request-nosign-btn"
                  title={doc.nosign_request?.status === "denied" ? "Previous request denied — request again" : "Ask admin to allow downloading without your signature"}>
                  {doc.nosign_request?.status === "denied" ? "NO-SIGN DENIED · RE-REQUEST" : "REQUEST NO-SIGN COPY"}
                </button>
              )
            )}
            <button
              onClick={() => setShowPreview(true)}
              className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-[#EAF3EE]"
              data-testid="doc-view-pdf"
              title="Read the document in a private in-page popup (view-only)"
            >
              <FileText className="w-3 h-3" /> VIEW
            </button>
            {isAdmin && (doc.status === "completed" || doc.signed_at_employee) && (
              <label
                className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 cursor-pointer bg-[#FBF3DE]"
                title="Replace the employee's signature image on this document (auto background-removal + enhancement applied)"
                data-testid="doc-fix-emp-sign"
              >
                ✎ FIX EMP SIGN
                <input type="file" accept="image/png,image/jpeg" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2MB image"); return; }
                    const r = new FileReader();
                    r.onload = async () => {
                      const tid = toast.loading("Replacing employee signature…");
                      try {
                        await api.put(`/hr/documents/${doc.id}/employee-signature`, { signature_b64: r.result });
                        toast.success("Employee signature replaced — re-download the PDF", { id: tid });
                        onChanged?.();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Replace failed", { id: tid });
                      }
                    };
                    r.readAsDataURL(f);
                    e.target.value = "";
                  }} />
              </label>
            )}
            {isAdmin && editable && (
              <button
                onClick={saveFields}
                disabled={saving}
                className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-amber-100 hover:bg-amber-200"
                data-testid="doc-quick-edit"
                title="Save the field values you've edited above without leaving this dialog"
              >
                <PenLine className="w-3 h-3" /> {saving ? "SAVING…" : "SAVE EDITS"}
              </button>
            )}
            {isAdmin && editable && nextInGroup && (
              <button
                onClick={saveAndNext}
                disabled={saving}
                className="brut-btn px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400"
                data-testid="doc-save-next"
                title={`Save this document and move to ${templates.find((t) => t.key === nextInGroup.template_type)?.title || "the next one"}`}
              >
                {saving ? "SAVING…" : `SAVE & NEXT → ${(templates.find((t) => t.key === nextInGroup.template_type)?.title || nextInGroup.template_type).toUpperCase()}`}
              </button>
            )}
            {isAdmin && !editable && doc.status === "completed" && (
              <button
                onClick={async () => {
                  if (!window.confirm(
                    `Unlock '${doc.template_type}' for editing?\n\n` +
                    "This will move the document back to 'draft' so admin can amend the field values. " +
                    "Previous signatures will be cleared and a fresh signing round is required."
                  )) return;
                  try {
                    await api.post(`/hr/documents/${doc.id}/unlock`);
                    toast.success("Document unlocked for editing");
                    onChanged();
                  } catch (e) { toast.error(e.response?.data?.detail || "Unlock failed"); }
                }}
                className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 bg-amber-100 hover:bg-amber-200"
                data-testid="doc-unlock-edit"
                title="Move this completed document back to draft so it can be re-edited"
              >
                <PenLine className="w-3 h-3" /> UNLOCK & EDIT
              </button>
            )}
            {doc.status === "draft" && (
              <button
                onClick={async () => {
                  if (!confirm("Delete this draft? Signed documents cannot be deleted; only drafts can.")) return;
                  try {
                    await api.delete(`/hr/documents/${doc.id}`);
                    toast.success("Draft deleted");
                    onClose();
                    onChanged();
                  } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                }}
                className="brut-btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1 border-rose-700 text-rose-700 hover:bg-rose-50"
                data-testid="doc-detail-delete">
                <Trash2 className="w-3 h-3" /> DELETE DRAFT
              </button>
            )}
          </div>
        </div>
      </div>
      {showPreview && <DocPreviewModal doc={doc} title={tpl?.title || doc.template_type} onClose={() => setShowPreview(false)} />}
    </div>
  );
}

export function SigStatus({ label, who, at, present }) {
  return (
    <div className={`brut-card p-2 ${present ? "bg-emerald-50 border-emerald-500" : "bg-zinc-50"}`}>
      <div className="label-mono text-zinc-500">{label}</div>
      <div className="text-xs font-mono flex items-center gap-1">
        {present ? <CheckCircle2 className="w-3 h-3 text-emerald-700" /> : <Clock className="w-3 h-3 text-zinc-400" />}
        <span className="truncate">{who}</span>
      </div>
      <div className="text-[9px] font-mono text-zinc-500">{at ? new Date(at).toLocaleString() : "pending"}</div>
    </div>
  );
}

